"use client";

import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map(item => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild className="relative px-0 py-2 text-sm font-medium text-white transition-colors duration-[var(--duration-medium)] hover:!bg-transparent hover:!text-white focus:!bg-transparent focus:!text-white after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all after:duration-[var(--duration-medium)] hover:after:w-full focus:after:w-full">
              <Link to={item.href || "#"}>{item.label}</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-background/95 backdrop-blur-sm transition-all duration-[var(--duration-medium)] ${isScrolled ? "h-16 shadow-soft" : "h-[88px]"}`}>
      <div className="container mx-auto h-full px-6">
        <div className="flex h-full items-center justify-between">
          <Link to="/#" className="flex-shrink-0">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1500413/img/management_101_llc_freight_logo.png" alt="MANAGEMENT 101 LLC" className="h-7" />
          </Link>

          <div className="hidden lg:flex flex-1 justify-center">
            <MenuProvider menu_id="3" className="flex items-center">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="text-foreground transition-transform duration-[var(--duration-fast)] hover:!bg-transparent hover:scale-110 focus:!bg-transparent active:scale-95" aria-label="Search">
              <Search className="size-5" />
            </Button>

            <Button asChild className="hidden sm:inline-flex transition-transform duration-[var(--duration-fast)] hover:scale-105 active:scale-95">
              <Link to="#contact">Get a Quote</Link>
            </Button>
          </div>
        </div>
      </div>
    </header>;
}